import React, { createContext, useContext, useEffect, useState } from 'react';
import { DEFAULT_THEME, THEME_STORAGE_KEY, THEME_VARIANTS } from './constants.js';

const ThemeContext = createContext(undefined);

// Fallback hook that returns default theme if ThemeProvider is removed
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    // Return a dummy context that uses default theme if provider is removed
    return {
      currentTheme: DEFAULT_THEME,
      themeVariant: 'purple',
      setThemeVariant: () => {}, // No-op function
      setTheme: () => {}, // No-op function (compat alias)
    };
  }
  return context;
}

// Optional provider - can be removed without breaking the app
export function ThemeProvider({ children }) {
  const [themeVariant, setThemeVariantState] = useState(() => {
    // Always default to purple unless the user explicitly switched
    return 'purple';
  });

  // Apply initial theme class
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const root = document.documentElement;
      console.log('🎨 Initial theme application:', `theme-${themeVariant}`);
      console.log('🎨 HTML element before:', root.className);
      root.classList.remove('theme-purple', 'theme-teal');
      root.classList.add(`theme-${themeVariant}`);
      console.log('🎨 HTML element after:', root.className);
    }
  }, []);

  useEffect(() => {
    // Only run on client side to avoid SSR errors
    if (typeof window === 'undefined') return;
    
    localStorage.setItem(THEME_STORAGE_KEY, themeVariant);
    
    // Apply theme class to document root with smooth transition
    const root = document.documentElement;
    root.classList.add('theme-transition');
    root.classList.remove('theme-purple', 'theme-teal');
    root.classList.add(`theme-${themeVariant}`);
    
    // Remove transition class after animation completes
    setTimeout(() => {
      root.classList.remove('theme-transition');
    }, 150);
    
    // Debug: log what theme class was applied
    console.log('🎨 Theme applied:', `theme-${themeVariant}`);
    console.log('🎨 HTML classes:', root.className);
  }, [themeVariant]);

  const value = {
    currentTheme: THEME_VARIANTS[themeVariant],
    themeVariant,
    setThemeVariant: setThemeVariantState,
    // Compat alias expected by some components
    setTheme: (variant) => setThemeVariantState(variant),
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}
